import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AlertTriangle, Check, Copy, Download, Pencil, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import {
  DettaglioCassaforteRitiri,
  InvioEmailRitiro,
  MotivoBozza,
  RichiestaRitiro,
  StatoRichiestaRitiro,
  cambiaStatoRichiesta,
  chiudiInvioRitiro,
  creaRichiesta,
  creaSollecito,
  formattaDataAznext,
  generaBozzaCliente,
  inviaEmailRitiro,
  modificaBozzaVigilanza,
  scaricaEml,
} from "../../api/ritiri";
import { CardRitiri, PulsanteRitiri } from "../../components/ritiri/TestataRitiri";
import { EmailPreview } from "../Settings/EmailTemplates/EmailPreview";
import { giornoBreve } from "./presentazione";

/**
 * Richieste di ritiro nel dettaglio della cassaforte (§ 7.3, v1.14):
 * avanzamento, preparazione della bozza, schede "Email vigilanza" e "Email
 * cliente" con Copia testo e Scarica bozza (.eml), approvazione, invio
 * dichiarato, conferma, annullamento e sollecito. Dal § 8 bis: bozza alla
 * vigilanza riscritta prima dell'approvazione, tasto Invia con conferma e
 * tentativi di invio, con la chiusura a mano per gli admin.
 *
 * Che cosa si può fare lo dice il backend (`azioni`, `bozze_utilizzabili`,
 * `puo_sollecitare`, `bozze.motivi`): qui si mostra, non si ricalcola. I
 * permessi nascondono i pulsanti a chi se li vedrebbe rifiutare, ma il
 * controllo vero resta sul server.
 *
 * L'app spedisce solo dal tasto Invia, dopo la conferma di chi lo preme
 * (§ 8 bis); altrimenti prepara il testo e il file, e una persona spedisce da
 * Outlook. La conferma è un pannello dentro la richiesta, non una finestra:
 * sul telefono il dettaglio è già una finestra, e due insieme non si aprono.
 */

const FRASE_INVIO = "L'app prepara solo la bozza: l'invio lo fa una persona da Outlook";
const FRASE_INVIO_ATTIVO = "Si spedisce con Invia, dopo l'approvazione, oppure da Outlook";

/** Gli stati in cui il tasto Invia compare: approvata, e inviata o confermata per un reinvio. */
const STATI_INVIO: StatoRichiestaRitiro[] = ["approvata", "inviata", "confermata"];

const campo =
  "mt-1 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

/** "a@x.it, b@y.it" → ["a@x.it", "b@y.it"]: il controllo vero lo fa il backend. */
function indirizzi(testo: string): string[] {
  return testo
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
}

const PASSI: { stato: StatoRichiestaRitiro | "suggerita"; nome: string }[] = [
  { stato: "suggerita", nome: "Suggerita" },
  { stato: "bozza", nome: "Bozza" },
  { stato: "approvata", nome: "Approvata" },
  { stato: "inviata", nome: "Inviata" },
  { stato: "confermata", nome: "Confermata" },
  { stato: "eseguita", nome: "Eseguita" },
];

const APERTI: StatoRichiestaRitiro[] = ["bozza", "approvata", "inviata", "confermata"];

/**
 * Il testo di una bozza che non si è potuta salvare perché un altro utente
 * l'aveva salvata prima (409). Sta qui, fuori dai componenti, perché dopo il
 * 409 il dettaglio si rilegge e l'editor si smonta e rimonta: così riparte con
 * il testo dell'utente, accanto alla versione salvata nel frattempo. Si toglie
 * a salvataggio riuscito o con Annulla; un ricaricamento della pagina lo perde.
 */
type BozzaNonSalvata = { a: string; cc: string; oggetto: string; testo: string };
const bozzeNonSalvate = new Map<string, BozzaNonSalvata>();

/** I tentativi d'invio non risolti: restano visibili ovunque, finché un admin non li chiude. */
const nonRisolti = (q: RichiestaRitiro) => q.invii.filter((t) => t.esito === "in_invio");

const NOMI_STATO: Record<StatoRichiestaRitiro, string> = {
  bozza: "Bozza",
  approvata: "Approvata",
  inviata: "Inviata",
  confermata: "Confermata",
  eseguita: "Eseguita",
  annullata: "Annullata",
};

/** "Eseguita nei tempi", "in ritardo di 2 giorni"… */
export function testoEsecuzione(q: Pick<RichiestaRitiro, "esecuzione" | "giorni_ritardo">): string | null {
  if (q.esecuzione === "anticipato") return "Eseguita in anticipo";
  if (q.esecuzione === "nei_tempi") return "Eseguita nei tempi";
  if (q.esecuzione === "in_ritardo") return `Eseguita in ritardo di ${q.giorni_ritardo ?? "?"} ${q.giorni_ritardo === 1 ? "giorno" : "giorni"}`;
  return null;
}

function dataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Rome" });
}

interface Props {
  d: DettaglioCassaforteRitiri;
  /** Dopo ogni scrittura: il dettaglio (e la situazione) si rileggono. */
  onCambiata: () => void;
  /** Apre le impostazioni della cassaforte (per "Manca email cliente"). */
  onImpostazioni: () => void;
}

/**
 * "Manca codice GDO", "Manca email cliente"…: il testo del backend, con il link
 * alla scheda dove si completa il dato.
 */
function MotivoConLink({ m, deviceId, onImpostazioni }: { m: MotivoBozza; deviceId: string | null; onImpostazioni: () => void }) {
  const link = "ml-2 font-semibold text-primary underline underline-offset-2 hover:text-primary-light";
  return (
    <li data-motivo={m.codice}>
      {m.testo}
      {m.dove === "anagrafica_device" && deviceId && (
        <Link to={`/devices/${deviceId}`} className={link}>
          Completa l'anagrafica
        </Link>
      )}
      {m.dove === "impostazioni_cassaforte" && (
        <button type="button" onClick={onImpostazioni} className={link}>
          Apri le impostazioni
        </button>
      )}
      {m.dove === "impostazioni_sistema" && (
        <Link to="/settings/system" className={link}>
          Impostazioni di sistema
        </Link>
      )}
    </li>
  );
}

/** Senza doppioni: i motivi della cassaforte possono ricomparire tra quelli della relazione. */
function unici(motivi: MotivoBozza[]): MotivoBozza[] {
  return motivi.filter((m, i) => motivi.findIndex((x) => x.codice === m.codice) === i);
}

/**
 * I motivi bloccanti divisi per bozza (v1.16): quelli della bozza alla
 * vigilanza fermano la richiesta, quelli della bozza al cliente solo l'avviso
 * al cliente.
 */
function MotiviPerBozza({
  vigilanza,
  cliente,
  deviceId,
  onImpostazioni,
}: {
  vigilanza: MotivoBozza[];
  cliente: MotivoBozza[];
  deviceId: string | null;
  onImpostazioni: () => void;
}) {
  const gruppo = (titolo: string, chiave: string, motivi: MotivoBozza[], nota: string) =>
    motivi.length > 0 && (
      <div className="rounded-xl border border-ritiri-bordo bg-ritiri-th-fondo p-3.5 text-sm text-ink" role="note" data-bozza-bloccata={chiave}>
        <p className="font-semibold">{titolo}</p>
        <p className="text-xs text-ritiri-th-testo">{nota}</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {unici(motivi).map((m) => (
            <MotivoConLink key={m.codice} m={m} deviceId={deviceId} onImpostazioni={onImpostazioni} />
          ))}
        </ul>
      </div>
    );
  return (
    <>
      {gruppo("Email vigilanza: la bozza non si genera", "vigilanza", vigilanza, "Senza la bozza alla vigilanza la richiesta non si prepara.")}
      {gruppo(
        "Email cliente: la bozza non si genera",
        "cliente",
        cliente,
        "La richiesta si prepara lo stesso: la vigilanza si può contattare comunque."
      )}
    </>
  );
}

export function RichiesteCassaforte({ d, onCambiata, onImpostazioni }: Props) {
  const { hasPermission } = useAuth();
  const puoLavorare = hasPermission("ritiri:update");
  const aperte = d.richieste.filter((q) => APERTI.includes(q.stato));
  const passate = d.richieste.filter((q) => !APERTI.includes(q.stato));
  const principale = aperte.find((q) => q.tipo !== "sollecito") ?? null;

  return (
    <CardRitiri titolo="Richiesta di ritiro">
      <Avanzamento
        stato={principale?.stato ?? (d.bozze.tipo_nuova_richiesta === "suggerito" ? "suggerita" : null)}
        extra={principale?.tipo === "extra"}
        approvazioneObbligatoria={d.bozze.approvazione_obbligatoria}
      />

      <div className="mt-4 space-y-5">
        {aperte.map((q) => (
          <BloccoRichiesta key={q.id} q={q} puoLavorare={puoLavorare} onCambiata={onCambiata} deviceId={d.bozze.device_id} onImpostazioni={onImpostazioni} />
        ))}
        {/* Con una richiesta già aperta se ne può preparare un'altra, ma solo come ritiro extra con il motivo (v1.16). */}
        {puoLavorare && <NuovaRichiesta key={principale?.id ?? "nessuna"} d={d} aperta={principale} onCreata={onCambiata} onImpostazioni={onImpostazioni} />}
        {!principale && !puoLavorare && <p className="text-sm text-ritiri-th-testo">Nessuna richiesta aperta per questa cassaforte.</p>}
      </div>

      {passate.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">Richieste precedenti</h3>
          <ul className="divide-y divide-ritiri-bordo rounded-xl border border-ritiri-bordo text-sm" data-richieste="precedenti">
            {passate.map((q) => (
              <li key={q.id} className="px-3 py-2" data-richiesta-precedente={q.id}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span>
                    {q.tipo === "sollecito" ? "Sollecito per il ritiro del " : "Ritiro del "}
                    <span className="font-plex">{giornoBreve(q.data_ritiro_richiesta)}</span>
                  </span>
                  <span className="text-ritiri-th-testo">
                    {q.stato === "annullata" ? `Annullata${q.annullata?.motivo ? `: ${q.annullata.motivo}` : ""}` : testoEsecuzione(q) ?? NOMI_STATO[q.stato]}
                  </span>
                </div>
                {/* Annullata (anche in automatico, con l'originale) mentre un invio era in corso o
                    incerto: il tentativo resta da verificare e da chiudere, qui come nelle aperte. */}
                {nonRisolti(q).length > 0 && (
                  <InviiEmail invii={nonRisolti(q)} titolo="Invio da verificare" className="mt-2" onCambiata={onCambiata} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </CardRitiri>
  );
}

// ----------------------------------------------------------------------------
// Avanzamento (Suggerita → Bozza → Approvata → Inviata → Confermata → Eseguita)
// ----------------------------------------------------------------------------

function Avanzamento({
  stato,
  extra,
  approvazioneObbligatoria,
}: {
  stato: StatoRichiestaRitiro | "suggerita" | null;
  /** Ritiro extra: non l'ha suggerito il calcolo, il primo passo si chiama "Extra". */
  extra: boolean;
  approvazioneObbligatoria: boolean;
}) {
  const indice = stato ? PASSI.findIndex((p) => p.stato === stato) : -1;
  return (
    <ol className="flex flex-wrap gap-1.5" aria-label="Avanzamento della richiesta" data-avanzamento={stato ?? "nessuna"}>
      {PASSI.map((p, i) => (
        <li
          key={p.stato}
          aria-current={i === indice ? "step" : undefined}
          className={clsx(
            "rounded-full border px-2.5 py-1 text-xs font-semibold",
            i === indice ? "border-primary bg-primary text-white" : i < indice ? "border-primary/40 bg-ritiri-th-fondo text-ink" : "border-ritiri-bordo text-ritiri-th-testo"
          )}
        >
          {p.stato === "suggerita" && extra ? "Extra" : p.nome}
          {p.stato === "approvata" && !approvazioneObbligatoria && <span className="font-normal"> (facoltativa)</span>}
        </li>
      ))}
    </ol>
  );
}

// ----------------------------------------------------------------------------
// Nuova richiesta
// ----------------------------------------------------------------------------

function NuovaRichiesta({
  d,
  aperta,
  onCreata,
  onImpostazioni,
}: {
  d: DettaglioCassaforteRitiri;
  /** La richiesta già aperta, se c'è: la nuova è un ritiro extra e il motivo è obbligatorio (lo impone anche il backend). */
  aperta: RichiestaRitiro | null;
  onCreata: () => void;
  onImpostazioni: () => void;
}) {
  const { showToast } = useToast();
  const b = d.bozze;
  const [relazione, setRelazione] = useState(b.relazioni.length === 1 ? b.relazioni[0].id : "");
  const [motivo, setMotivo] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const unica = b.relazioni.length === 1 ? b.relazioni[0] : null;
  if (b.motivi.length > 0) {
    // Motivi della cassaforte: bloccano tutto. Con una sola relazione si dicono
    // anche i suoi, bozza per bozza, tutti insieme.
    return (
      <div className="space-y-2" data-bozze="bloccate">
        <MotiviPerBozza
          vigilanza={[...b.motivi, ...(unica?.motivi_vigilanza ?? [])]}
          cliente={[...b.motivi, ...(unica?.motivi_cliente ?? [])]}
          deviceId={b.device_id}
          onImpostazioni={onImpostazioni}
        />
      </div>
    );
  }

  const scelta = b.relazioni.find((r) => r.id === relazione) ?? null;
  const motiviVigilanza = scelta?.motivi_vigilanza ?? [];
  const motiviCliente = scelta?.motivi_cliente ?? [];

  async function prepara() {
    setInCorso(true);
    setErrore(null);
    try {
      await creaRichiesta({ id_macchina: d.id_macchina, device_cliente_id: relazione || undefined, motivo_extra: motivo.trim() || undefined });
      showToast("Bozza preparata.", "success");
      setMotivo("");
      onCreata();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile preparare la bozza."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className={clsx("space-y-3", aperta && "rounded-xl border border-ritiri-bordo p-4")} data-bozze={aperta ? "extra" : "nuova"}>
      {aperta ? (
        <>
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">Ritiro extra</h3>
          <p className="text-sm font-semibold text-ink" data-avviso="richiesta-aperta">
            C'è già una richiesta aperta ({NOMI_STATO[aperta.stato].toLowerCase()}, ritiro del {giornoBreve(aperta.data_ritiro_richiesta)}).
          </p>
          <p className="text-sm text-ink">Un'altra richiesta per questa cassaforte è un ritiro extra: serve il motivo.</p>
        </>
      ) : (
        <p className="text-sm text-ink">
          {b.tipo_nuova_richiesta === "suggerito"
            ? "Nessuna richiesta aperta: si può preparare la bozza per il primo ritiro utile."
            : "Nessuna richiesta aperta. L'esito non suggerisce un ritiro: la richiesta sarà un ritiro extra."}
        </p>
      )}
      {b.scelta_necessaria && (
        <label className="block text-xs font-bold text-ritiri-th-testo">
          Più clienti con dati diversi: relazione da usare per le bozze
          <select
            className="mt-1 h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink"
            value={relazione}
            onChange={(e) => setRelazione(e.target.value)}
          >
            <option value="">Scegli la relazione…</option>
            {b.relazioni.map((r) => (
              <option key={r.id} value={r.id} disabled={r.motivi_vigilanza.length > 0}>
                {r.cliente}
                {r.sede ? ` · ${r.sede}` : ""}
                {r.codice_gdo ? ` · GDO ${r.codice_gdo}` : ""}
                {r.motivi_vigilanza.length > 0 ? " (non utilizzabile)" : ""}
              </option>
            ))}
          </select>
        </label>
      )}
      {b.scelta_necessaria && b.relazioni.some((r) => r.motivi_vigilanza.length > 0) && (
        <ul className="space-y-0.5 text-xs text-ritiri-th-testo">
          {b.relazioni
            .filter((r) => r.motivi_vigilanza.length > 0)
            .map((r) => (
              <li key={r.id}>
                {r.cliente}: {r.motivi_vigilanza.map((m) => m.testo).join("; ")}
              </li>
            ))}
        </ul>
      )}
      {scelta && <MotiviPerBozza vigilanza={motiviVigilanza} cliente={motiviCliente} deviceId={b.device_id} onImpostazioni={onImpostazioni} />}
      {(aperta || b.tipo_nuova_richiesta === "extra") && (
        <label className="block text-xs font-bold text-ritiri-th-testo">
          Motivo del ritiro extra ({aperta ? "obbligatorio" : "facoltativo"})
          <input
            type="text"
            maxLength={500}
            required={!!aperta}
            className="mt-1 h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
      )}
      {errore && (
        <p className="text-sm text-danger" role="alert">
          {errore}
        </p>
      )}
      <PulsanteRitiri
        type="button"
        variante={aperta ? "secondario" : "principale"}
        onClick={prepara}
        inCorso={inCorso}
        disabled={!relazione || motiviVigilanza.length > 0 || (!!aperta && motivo.trim() === "")}
      >
        {aperta ? "Prepara il ritiro extra" : "Prepara la bozza"}
      </PulsanteRitiri>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Una richiesta aperta: stato, avvisi, email e azioni
// ----------------------------------------------------------------------------

function BloccoRichiesta({
  q,
  puoLavorare,
  onCambiata,
  deviceId,
  onImpostazioni,
}: {
  q: RichiestaRitiro;
  puoLavorare: boolean;
  onCambiata: () => void;
  deviceId: string | null;
  onImpostazioni: () => void;
}) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [annulla, setAnnulla] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [scheda, setScheda] = useState<"vigilanza" | "cliente">("vigilanza");
  const [conferma, setConferma] = useState(false);
  const puoInviare = hasPermission("ritiri:invia") && q.invio.abilitato && STATI_INVIO.includes(q.stato) && q.vigilanza.oggetto !== null;

  async function esegui(chiave: string, azione: () => Promise<unknown>, messaggio: string) {
    setInCorso(chiave);
    setErrore(null);
    try {
      await azione();
      showToast(messaggio, "success");
      onCambiata();
    } catch (err) {
      setErrore(getErrorMessage(err, "Operazione non riuscita."));
    } finally {
      setInCorso(null);
    }
  }

  const puo = (a: RichiestaRitiro["azioni"][number]) => puoLavorare && q.azioni.includes(a) && (a !== "approvata" || hasPermission("ritiri:approva"));
  const titolo = q.tipo === "sollecito" ? `Sollecito per il ritiro di ${giornoBreve(q.data_ritiro_richiesta)}` : `Ritiro di ${giornoBreve(q.data_ritiro_richiesta)}`;

  return (
    <div className="rounded-xl border border-ritiri-bordo p-4" data-richiesta={q.id} data-stato={q.stato}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-ink">{titolo}</h3>
        <span className="rounded-full bg-ritiri-th-fondo px-2.5 py-1 text-xs font-semibold text-ink">
          {NOMI_STATO[q.stato]}
          {q.tipo === "extra" && " · extra"}
        </span>
      </div>
      <p className="mt-1 text-xs text-ritiri-th-testo">
        Preparata {dataOra(q.creata.il)}
        {q.creata.da ? ` da ${q.creata.da}` : ""}
        {q.approvata && ` · approvata ${dataOra(q.approvata.il)}${q.approvata.da ? ` da ${q.approvata.da}` : ""}`}
        {q.inviata && ` · inviata ${dataOra(q.inviata.il)}${q.inviata.da ? ` da ${q.inviata.da}` : ""}`}
        {q.confermata && ` · confermata ${dataOra(q.confermata.il)}`}
        {q.relazione && ` · cliente ${q.relazione.cliente}`}
      </p>
      {q.motivo_extra && <p className="mt-1 text-sm text-ink">Motivo: {q.motivo_extra}</p>}

      {q.non_effettuata && (
        <div className="mt-3 flex gap-2 rounded-xl border border-danger bg-danger/10 p-3 text-sm text-ink" role="alert" data-avviso="non-effettuato">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-bold">Ritiro non effettuato</p>
            <p>
              Il ritiro richiesto per {giornoBreve(q.data_ritiro_richiesta)} non risulta su AZNext, e i dati di quel giorno sono completi.
            </p>
            {q.puo_sollecitare && puoLavorare && (
              <PulsanteRitiri
                type="button"
                variante="secondario"
                className="mt-2 h-9 px-3.5"
                inCorso={inCorso === "sollecito"}
                onClick={() => esegui("sollecito", () => creaSollecito(q.id), "Bozza di sollecito preparata.")}
              >
                Prepara sollecito alla vigilanza
              </PulsanteRitiri>
            )}
          </div>
        </div>
      )}

      {q.non_verificabile && (
        <p className="mt-3 flex gap-2 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" role="note" data-avviso="non-verificabile">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-dark" aria-hidden="true" />
          <span>{q.non_verificabile}: senza i suoi dati il ritiro non si può verificare, e non parte nessuna segnalazione.</span>
        </p>
      )}

      {q.avvisi.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-xl bg-ritiri-th-fondo p-3 text-sm text-ink" data-avvisi>
          {q.avvisi.map((a) => (
            <li key={a} className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-dark" aria-hidden="true" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Schede delle email: il sollecito va solo alla vigilanza. */}
      <div className="mt-4">
        {q.tipo !== "sollecito" && (
          <div role="tablist" aria-label="Bozze email" className="mb-3 flex gap-2">
            {(["vigilanza", "cliente"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={scheda === s}
                onClick={() => setScheda(s)}
                className={clsx(
                  "rounded-full px-3 py-1.5 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary",
                  scheda === s ? "bg-primary text-white" : "border border-ritiri-bordo-forte text-ink hover:bg-ritiri-hover"
                )}
              >
                {s === "vigilanza" ? "Email vigilanza" : "Email cliente"}
              </button>
            ))}
          </div>
        )}
        {q.tipo !== "sollecito" && scheda === "cliente" && !q.cliente ? (
          // Bozza al cliente non generata (v1.16): il motivo, il link per completare
          // il dato e, finché il ritiro non è eseguito né annullato, il comando per generarla.
          <div role="tabpanel" aria-label="Email cliente" className="space-y-3" data-email="cliente-bloccata">
            <MotiviPerBozza vigilanza={[]} cliente={q.cliente_bloccata ?? []} deviceId={deviceId} onImpostazioni={onImpostazioni} />
            {q.puo_generare_cliente && puoLavorare && (
              <PulsanteRitiri
                type="button"
                variante="secondario"
                className="h-9 px-3.5"
                inCorso={inCorso === "bozza-cliente"}
                onClick={() => esegui("bozza-cliente", () => generaBozzaCliente(q.id), "Bozza al cliente generata.")}
              >
                Genera la bozza al cliente
              </PulsanteRitiri>
            )}
          </div>
        ) : (
          <SchedaEmail
            q={q}
            tipo={q.tipo === "sollecito" ? "vigilanza" : scheda}
            titolo={q.tipo === "sollecito" ? "Email vigilanza" : undefined}
            puoScaricare={puoLavorare}
            puoModificare={puoLavorare && q.bozza_modificabile}
            onCambiata={onCambiata}
          />
        )}
        {/* Nella scheda vigilanza tutti i tentativi; in quella cliente solo quelli da chiudere, che non si perdono di vista. */}
        {(q.tipo === "sollecito" || scheda === "vigilanza")
          ? q.invii.length > 0 && <InviiEmail invii={q.invii} onCambiata={onCambiata} />
          : nonRisolti(q).length > 0 && <InviiEmail invii={nonRisolti(q)} titolo="Invio alla vigilanza da verificare" onCambiata={onCambiata} />}
      </div>

      {errore && (
        <p className="mt-3 text-sm text-danger" role="alert">
          {errore}
        </p>
      )}

      {(puoLavorare || puoInviare) && !conferma && (
        <div className="mt-4 flex flex-wrap gap-2" data-azioni>
          {puoInviare && (
            <PulsanteRitiri
              type="button"
              className="h-9 px-3.5"
              onClick={() => setConferma(true)}
              disabled={!q.invio.inviabile}
              title={q.invio.motivo ?? undefined}
              data-invio={q.invio.gia_inviata ? "reinvia" : "invia"}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              {q.invio.gia_inviata ? "Reinvia" : "Invia"}
            </PulsanteRitiri>
          )}
          {puo("approvata") && (
            <PulsanteRitiri
              type="button"
              className="h-9 px-3.5"
              inCorso={inCorso === "approvata"}
              onClick={() => esegui("approvata", () => cambiaStatoRichiesta(q.id, { stato: "approvata", versione: q.versione }), "Bozza approvata.")}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              Approva
            </PulsanteRitiri>
          )}
          {puoLavorare && q.stato === "bozza" && q.azioni.includes("approvata") && !hasPermission("ritiri:approva") && !q.azioni.includes("inviata") && (
            <p className="self-center text-xs text-ritiri-th-testo">In attesa di approvazione da chi ha il permesso ritiri:approva.</p>
          )}
          {puo("inviata") && (
            <PulsanteRitiri
              type="button"
              className="h-9 px-3.5"
              inCorso={inCorso === "inviata"}
              onClick={() => esegui("inviata", () => cambiaStatoRichiesta(q.id, { stato: "inviata" }), "Segnata come inviata.")}
            >
              Segna come inviata
            </PulsanteRitiri>
          )}
          {puo("confermata") && (
            <PulsanteRitiri
              type="button"
              variante="secondario"
              className="h-9 px-3.5"
              inCorso={inCorso === "confermata"}
              onClick={() => esegui("confermata", () => cambiaStatoRichiesta(q.id, { stato: "confermata" }), "Segnata come confermata dalla vigilanza.")}
            >
              Confermata dalla vigilanza
            </PulsanteRitiri>
          )}
          {puo("annullata") && !annulla && (
            <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => setAnnulla(true)}>
              Annulla richiesta
            </PulsanteRitiri>
          )}
        </div>
      )}
      {conferma && (
        <ConfermaInvio
          q={q}
          onChiudi={() => setConferma(false)}
          onInviata={() => {
            setConferma(false);
            onCambiata();
          }}
          onFallito={() => {
            setConferma(false);
            onCambiata();
          }}
        />
      )}
      {annulla && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          {/* Il backend annulla con la richiesta i suoi solleciti non chiusi: si dice prima di confermare. */}
          {q.solleciti_aperti > 0 && (
            <p className="flex w-full gap-2 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" role="note" data-avviso="solleciti-aperti">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-dark" aria-hidden="true" />
              <span>
                {q.solleciti_aperti === 1
                  ? "Annullando la richiesta si annulla anche il sollecito aperto collegato."
                  : `Annullando la richiesta si annullano anche i ${q.solleciti_aperti} solleciti aperti collegati.`}
              </span>
            </p>
          )}
          <label className="block min-w-[14rem] flex-1 text-xs font-bold text-ritiri-th-testo">
            Motivo dell'annullamento (facoltativo)
            <input
              type="text"
              maxLength={500}
              className="mt-1 h-9 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <PulsanteRitiri
            type="button"
            className="h-9 px-3.5"
            inCorso={inCorso === "annullata"}
            onClick={() =>
              esegui("annullata", () => cambiaStatoRichiesta(q.id, { stato: "annullata", motivo_annullamento: motivo.trim() || undefined }), "Richiesta annullata.")
            }
          >
            Conferma annullamento
          </PulsanteRitiri>
          <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => setAnnulla(false)}>
            Indietro
          </PulsanteRitiri>
        </div>
      )}
      {q.prelievo_il && <p className="mt-2 text-xs text-ritiri-th-testo">Ritiro registrato su AZNext il {formattaDataAznext(q.prelievo_il)}.</p>}
    </div>
  );
}

function SchedaEmail({
  q,
  tipo,
  titolo,
  puoScaricare,
  puoModificare,
  onCambiata,
}: {
  q: RichiestaRitiro;
  tipo: "vigilanza" | "cliente";
  titolo?: string;
  puoScaricare: boolean;
  /** La bozza alla vigilanza si riscrive (in bozza, con ritiri:update). */
  puoModificare: boolean;
  onCambiata: () => void;
}) {
  const { showToast } = useToast();
  const [scarico, setScarico] = useState(false);
  // Riaperta da sola dopo un 409, con il testo dell'utente (vedi `bozzeNonSalvate`).
  const [modifica, setModifica] = useState(() => tipo === "vigilanza" && puoModificare && bozzeNonSalvate.has(q.id));
  const [, aggiorna] = useState(0);
  const persa = tipo === "vigilanza" && !puoModificare ? bozzeNonSalvate.get(q.id) ?? null : null;
  const email = tipo === "vigilanza" ? q.vigilanza : q.cliente!;
  const bloccata = !q.bozze_utilizzabili;

  /**
   * Copia l'HTML (grassetti e firma, come in Outlook) insieme al testo
   * semplice: chi incolla in Outlook riceve l'HTML, un editor di testo il
   * testo. Dove il browser non sa copiare HTML si ripiega sul solo testo.
   */
  async function copia() {
    try {
      // navigator.clipboard esiste solo in contesto sicuro (https o localhost).
      if (!navigator.clipboard) throw new Error("clipboard non disponibile");
      const testo = email.corpo ?? "";
      if (email.html && typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([email.html], { type: "text/html" }),
            "text/plain": new Blob([testo], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(testo);
      }
      showToast("Testo copiato.", "success");
    } catch {
      showToast("Copia non riuscita: selezionare il testo a mano.", "error");
    }
  }

  async function scarica() {
    setScarico(true);
    try {
      const { blob, nome } = await scaricaEml(q.id, tipo);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scaricare la bozza."), "error");
    } finally {
      setScarico(false);
    }
  }

  const riga = (nome: string, valore: string) => (
    <div className="grid gap-1 sm:grid-cols-[5rem_minmax(0,1fr)]">
      <dt className="text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">{nome}</dt>
      <dd className="break-words text-ink">{valore}</dd>
    </div>
  );

  if (modifica && tipo === "vigilanza") {
    return (
      <ModificaBozza
        // Una versione nuova (dopo un 409) rimonta l'editor: riparte dal testo non salvato.
        key={q.versione}
        q={q}
        onChiudi={() => setModifica(false)}
        onSalvata={() => {
          setModifica(false);
          onCambiata();
        }}
        onConflitto={onCambiata}
      />
    );
  }

  return (
    <div role={titolo ? undefined : "tabpanel"} aria-label={titolo ?? (tipo === "vigilanza" ? "Email vigilanza" : "Email cliente")} data-email={tipo}>
      {titolo && <h4 className="mb-2 text-sm font-bold text-ink">{titolo}</h4>}
      <dl className="space-y-1.5 text-sm">
        {riga("Da", `${email.da.nome} <${email.da.indirizzo}>`)}
        {riga("A", email.a.join(", ") || "—")}
        {tipo === "vigilanza" && riga("Cc", email.cc.join(", ") || "—")}
        {riga("Oggetto", email.oggetto ?? "—")}
      </dl>
      <div className="mt-3" data-corpo>
        {email.html ? (
          <EmailPreview html={email.html} titolo={`Anteprima ${tipo === "vigilanza" ? "email vigilanza" : "email cliente"}`} />
        ) : (
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-ritiri-bordo bg-ritiri-th-fondo p-3 font-sans text-sm text-ink">
            {email.corpo}
          </pre>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={copia} disabled={bloccata}>
          <Copy className="h-4 w-4" aria-hidden="true" />
          Copia testo
        </PulsanteRitiri>
        {puoScaricare && (
          <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={scarica} disabled={bloccata} inCorso={scarico}>
            <Download className="h-4 w-4" aria-hidden="true" />
            Scarica bozza (.eml)
          </PulsanteRitiri>
        )}
        {tipo === "vigilanza" && puoModificare && (
          <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => setModifica(true)}>
            <Pencil className="h-4 w-4" aria-hidden="true" />
            Modifica bozza
          </PulsanteRitiri>
        )}
        <span className="text-xs text-ritiri-th-testo">{tipo === "vigilanza" && q.invio.abilitato ? FRASE_INVIO_ATTIVO : FRASE_INVIO}</span>
      </div>
      {persa && (
        // Il 409 era un'approvazione arrivata mentre si scriveva: la bozza non si
        // modifica più, ma il testo non salvato resta da copiare.
        <div className="mt-3 space-y-2 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" role="note" data-bozza="non-salvata">
          <p>La bozza è cambiata mentre la modificavi e ora non si modifica più: le tue modifiche non sono state salvate. Eccole, da copiare se servono.</p>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-sans text-xs">{`Oggetto: ${persa.oggetto}\n\n${persa.testo}`}</pre>
          <PulsanteRitiri
            type="button"
            variante="secondario"
            className="h-8 px-3"
            onClick={() => {
              bozzeNonSalvate.delete(q.id);
              aggiorna((n) => n + 1);
            }}
          >
            Ho capito
          </PulsanteRitiri>
        </div>
      )}
      {bloccata && q.stato !== "annullata" && (
        <p className="mt-2 text-xs font-semibold text-ritiri-th-testo" data-bozza="da-approvare">
          Copia testo e Scarica bozza si abilitano dopo l'approvazione.
        </p>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Bozza alla vigilanza riscritta a mano (§ 8 bis)
// ----------------------------------------------------------------------------

/**
 * Destinatari, oggetto e testo della bozza alla vigilanza, solo prima
 * dell'approvazione: chi approva vede il testo riscritto, e il tasto Invia
 * spedisce quello. Il backend controlla gli indirizzi e che l'oggetto contenga
 * ancora il codice GDO.
 */
function ModificaBozza({
  q,
  onChiudi,
  onSalvata,
  onConflitto,
}: {
  q: RichiestaRitiro;
  onChiudi: () => void;
  onSalvata: () => void;
  /** 409: la bozza è cambiata dopo l'apertura. Si rilegge; l'editor riparte con il testo dell'utente. */
  onConflitto: () => void;
}) {
  const { showToast } = useToast();
  // Dopo un 409 il modulo riparte con il testo dell'utente, non con quello salvato.
  const [ripresa] = useState(() => bozzeNonSalvate.get(q.id) ?? null);
  const [a, setA] = useState(ripresa?.a ?? q.vigilanza.a.join(", "));
  const [cc, setCc] = useState(ripresa?.cc ?? q.vigilanza.cc.join(", "));
  const [oggetto, setOggetto] = useState(ripresa?.oggetto ?? q.vigilanza.oggetto ?? "");
  const [testo, setTesto] = useState(ripresa?.testo ?? q.vigilanza.testo ?? "");
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const id = `modifica-bozza-${q.id}`;

  async function salva() {
    setInCorso(true);
    setErrore(null);
    try {
      // La versione della bozza aperta: se un altro l'ha salvata nel frattempo, 409 e nessuna scrittura.
      await modificaBozzaVigilanza(q.id, { a: indirizzi(a), cc: indirizzi(cc), oggetto, testo, versione: q.versione });
      bozzeNonSalvate.delete(q.id);
      showToast("Bozza modificata.", "success");
      onSalvata();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        bozzeNonSalvate.set(q.id, { a, cc, oggetto, testo });
        showToast(getErrorMessage(err, "La bozza è stata modificata da un altro utente: ricaricare."), "error");
        onConflitto();
        return;
      }
      setErrore(getErrorMessage(err, "Impossibile salvare la bozza."));
    } finally {
      setInCorso(false);
    }
  }

  function annulla() {
    bozzeNonSalvate.delete(q.id);
    onChiudi();
  }

  const etichetta = "block text-xs font-bold text-ritiri-th-testo";
  return (
    <div className="space-y-3" role="group" aria-labelledby={`${id}-titolo`} data-modifica-bozza>
      <h4 id={`${id}-titolo`} className="text-sm font-bold text-ink">
        Modifica la bozza alla vigilanza
      </h4>
      {ripresa && (
        <div className="space-y-2 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" role="alert" data-bozza="conflitto">
          <p className="font-semibold">La bozza è stata modificata da un altro utente mentre la scrivevi.</p>
          <p>
            Il modulo contiene il tuo testo, non ancora salvato. Qui sotto la versione salvata adesso: confrontale, poi salva quella giusta.
          </p>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white p-2 font-sans text-xs" data-bozza="salvata">
            {`A: ${q.vigilanza.a.join(", ")}\nCc: ${q.vigilanza.cc.join(", ") || "—"}\nOggetto: ${q.vigilanza.oggetto ?? ""}\n\n${q.vigilanza.testo ?? ""}`}
          </pre>
        </div>
      )}
      <label className={etichetta}>
        A (separati da virgola)
        <input type="text" className={clsx(campo, "h-10")} value={a} onChange={(e) => setA(e.target.value)} />
      </label>
      <label className={etichetta}>
        Cc (separati da virgola)
        <input type="text" className={clsx(campo, "h-10")} value={cc} onChange={(e) => setCc(e.target.value)} />
      </label>
      <label className={etichetta}>
        Oggetto (deve contenere il codice GDO)
        <input type="text" maxLength={255} className={clsx(campo, "h-10")} value={oggetto} onChange={(e) => setOggetto(e.target.value)} />
      </label>
      <label className={etichetta}>
        Testo
        <textarea rows={12} maxLength={10000} className={clsx(campo, "py-2 font-sans")} value={testo} onChange={(e) => setTesto(e.target.value)} />
      </label>
      <p className="text-xs text-ritiri-th-testo">La firma dell'ufficio ritiri si aggiunge da sola. Nel testo riscritto i grassetti non restano.</p>
      {errore && (
        <p className="text-sm text-danger" role="alert">
          {errore}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <PulsanteRitiri type="button" className="h-9 px-3.5" onClick={salva} inCorso={inCorso}>
          Salva la bozza
        </PulsanteRitiri>
        <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={annulla} disabled={inCorso}>
          Annulla
        </PulsanteRitiri>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Invio dall'app (§ 8 bis)
// ----------------------------------------------------------------------------

/**
 * La conferma prima dell'invio: mittente, destinatari, oggetto e corpo, come
 * partiranno. L'invio parte solo da "Conferma invio"; se l'email risulta già
 * inviata il pannello lo dice, e il pulsante è la seconda conferma.
 */
function ConfermaInvio({
  q,
  onChiudi,
  onInviata,
  onFallito,
}: {
  q: RichiestaRitiro;
  onChiudi: () => void;
  onInviata: () => void;
  /** Chiuso dopo un errore: la richiesta si rilegge, e il tentativo non riuscito compare fra gli invii. */
  onFallito: () => void;
}) {
  const { showToast } = useToast();
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const titolo = useRef<HTMLHeadingElement>(null);
  const id = `conferma-invio-${q.id}`;
  const v = q.vigilanza;
  const reinvio = q.invio.gia_inviata;

  // Il fuoco va sul titolo del pannello: chi usa la tastiera legge subito che cosa sta confermando.
  useEffect(() => titolo.current?.focus(), []);

  async function invia() {
    setInCorso(true);
    setErrore(null);
    try {
      await inviaEmailRitiro(q.id, reinvio ? { conferma: true, reinvio: true } : { conferma: true });
      showToast("Email inviata alla vigilanza.", "success");
      onInviata();
    } catch (err) {
      // Graph ha rifiutato o l'esito è incerto (502), o la richiesta è cambiata (409): l'errore resta
      // qui. Rileggere subito smonterebbe il pannello (il dettaglio torna a
      // "Caricamento…") e il messaggio con lui: si rilegge alla chiusura.
      setErrore(getErrorMessage(err, "Invio non riuscito."));
    } finally {
      setInCorso(false);
    }
  }

  const gia = q.invio.ultima_inviata ?? q.inviata;
  const riga = (nome: string, valore: string) => (
    <div className="grid gap-1 sm:grid-cols-[5rem_minmax(0,1fr)]">
      <dt className="text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">{nome}</dt>
      <dd className="break-words text-ink">{valore}</dd>
    </div>
  );
  return (
    <div className="mt-4 space-y-3 rounded-xl border border-primary p-4" role="group" aria-labelledby={`${id}-titolo`} data-conferma-invio={reinvio ? "reinvio" : "invio"}>
      <h4 id={`${id}-titolo`} ref={titolo} tabIndex={-1} className="font-bold text-ink focus:outline-none">
        {reinvio ? "Inviare di nuovo l'email alla vigilanza?" : "Inviare l'email alla vigilanza?"}
      </h4>
      {reinvio && (
        <p className="flex gap-2 rounded-xl border border-accent bg-accent/10 p-3 text-sm text-ink" role="note" data-avviso="gia-inviata">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-dark" aria-hidden="true" />
          <span>
            Questa email risulta già inviata{gia ? ` il ${dataOra(gia.il)}${gia.da ? ` da ${gia.da}` : ""}` : ""}. Confermando ne parte un'altra copia.
          </span>
        </p>
      )}
      <dl className="space-y-1.5 text-sm">
        {riga("Da", `${v.da.nome} <${v.da.indirizzo}>`)}
        {riga("A", v.a.join(", ") || "—")}
        {riga("Cc", v.cc.join(", ") || "—")}
        {riga("Oggetto", v.oggetto ?? "—")}
      </dl>
      {v.html && <EmailPreview html={v.html} titolo="Anteprima dell'email da inviare" />}
      <p className="text-xs text-ritiri-th-testo">Parte dalla casella {v.da.indirizzo}, che ne tiene la copia in Posta inviata.</p>
      {errore && (
        <p className="text-sm text-danger" role="alert">
          {errore}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <PulsanteRitiri type="button" className="h-9 px-3.5" onClick={invia} inCorso={inCorso}>
          <Send className="h-4 w-4" aria-hidden="true" />
          {reinvio ? "Conferma nuovo invio" : "Conferma invio"}
        </PulsanteRitiri>
        <PulsanteRitiri type="button" variante="secondario" className="h-9 px-3.5" onClick={() => (errore ? onFallito() : onChiudi())} disabled={inCorso}>
          {errore ? "Chiudi" : "Annulla"}
        </PulsanteRitiri>
      </div>
    </div>
  );
}

const NOMI_ESITO: Record<InvioEmailRitiro["esito"], string> = { in_invio: "In invio", inviata: "Inviata", errore: "Non inviata" };

/**
 * I tentativi di invio dall'app, dal più recente. Un tentativo rimasto in
 * invio (il processo si è fermato prima della risposta di Graph, o l'esito è
 * incerto) lo chiude a mano un admin, dopo aver cercato il messaggio in Posta
 * inviata. Anche su una richiesta già annullata o eseguita: lì si mostrano
 * solo i tentativi non risolti.
 */
function InviiEmail({
  invii,
  titolo = "Invii dall'app",
  className = "mt-4",
  onCambiata,
}: {
  invii: InvioEmailRitiro[];
  titolo?: string;
  className?: string;
  onCambiata: () => void;
}) {
  const { hasPermission } = useAuth();
  const puoChiudere = hasPermission("ritiri:chiudi_invio");
  return (
    <div className={className} data-invii>
      <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">{titolo}</h4>
      <ul className="divide-y divide-ritiri-bordo rounded-xl border border-ritiri-bordo text-sm">
        {invii.map((t) => (
          <li key={t.id} className="space-y-1 px-3 py-2" data-invio={t.esito}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-ink">
                {dataOra(t.iniziato_il)}
                {t.da ? ` · ${t.da}` : ""}
                {t.reinvio ? " · reinvio" : ""}
                {` · A ${t.a.length}${t.cc.length > 0 ? `, Cc ${t.cc.length}` : ""}`}
              </span>
              <span className={clsx("font-semibold", t.esito === "errore" ? "text-danger" : t.esito === "in_invio" ? "text-accent-dark" : "text-ink")}>
                {t.esito === "in_invio" && t.da_verificare ? "Da verificare in Posta inviata" : NOMI_ESITO[t.esito]}
              </span>
            </div>
            {t.errore && <p className="text-xs text-danger">{t.errore}</p>}
            {t.chiuso_a_mano && (
              <p className="text-xs text-ritiri-th-testo">
                Chiuso a mano {dataOra(t.chiuso_a_mano.il)}
                {t.chiuso_a_mano.da ? ` da ${t.chiuso_a_mano.da}` : ""}
              </p>
            )}
            {t.esito === "in_invio" && !t.da_verificare && <p className="text-xs text-ritiri-th-testo">Invio in corso: la risposta di Graph non è ancora arrivata.</p>}
            {t.esito === "in_invio" && t.da_verificare && puoChiudere && <ChiusuraAMano t={t} onCambiata={onCambiata} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChiusuraAMano({ t, onCambiata }: { t: InvioEmailRitiro; onCambiata: () => void }) {
  const { showToast } = useToast();
  const [nota, setNota] = useState("");
  const [inCorso, setInCorso] = useState<"inviata" | "non_inviata" | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  async function chiudi(esito: "inviata" | "non_inviata") {
    setInCorso(esito);
    setErrore(null);
    try {
      await chiudiInvioRitiro(t.id, { esito, nota: nota.trim() || undefined });
      showToast(esito === "inviata" ? "Segnata come inviata." : "Segnata come non inviata.", "success");
      onCambiata();
    } catch (err) {
      setErrore(getErrorMessage(err, "Operazione non riuscita."));
    } finally {
      setInCorso(null);
    }
  }

  return (
    <div className="space-y-2 rounded-xl bg-ritiri-th-fondo p-3" data-chiusura-a-mano>
      <p className="text-xs text-ink">
        {t.errore ? "L'esito dell'invio è incerto: l'email può essere partita." : "La risposta di Graph non è arrivata."} Cercare in Posta inviata di
        ritirovalori@winkon.it il messaggio con oggetto «{t.oggetto}» (intestazione X-WinkOn-Invio-Id: <span className="font-plex">{t.id}</span>), poi dire
        com'è andata.
      </p>
      <label className="block text-xs font-bold text-ritiri-th-testo">
        Nota (facoltativa)
        <input type="text" maxLength={500} className={clsx(campo, "h-9")} value={nota} onChange={(e) => setNota(e.target.value)} />
      </label>
      {errore && (
        <p className="text-sm text-danger" role="alert">
          {errore}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <PulsanteRitiri type="button" className="h-9 px-3.5" onClick={() => chiudi("inviata")} inCorso={inCorso === "inviata"} disabled={inCorso !== null}>
          Segnata come inviata
        </PulsanteRitiri>
        <PulsanteRitiri
          type="button"
          variante="secondario"
          className="h-9 px-3.5"
          onClick={() => chiudi("non_inviata")}
          inCorso={inCorso === "non_inviata"}
          disabled={inCorso !== null}
        >
          Segnata come non inviata
        </PulsanteRitiri>
      </div>
    </div>
  );
}
